import { useRef } from 'react'
import bgSpeed from '../../assets/images/service-bg.png'
import arrowRight from '../../assets/images/arrow-down.svg'
import ServiceButton from '../../components/ui/ServiceButton'

import imgZone from '../../assets/images/ser_1.png'
import imgTracking from '../../assets/images/ser_2.png'
import imgFullWidth from '../../assets/images/feat_img.png'
import imgPickup from '../../assets/images/ser_3.png'

import imgZone_mobile from '../../assets/images/ser_1_mobile.png'
import imgTracking_mobile from '../../assets/images/ser_2_mobile.png'
import imgPickup_mobile from '../../assets/images/ser_3_mobile.png'
import imgFullWidth_mobile from '../../assets/images/feat_img_mobile.png'

const LETS_TALK_URL =
  'https://outlook.office365.com/book/LetsTalk@Speedyssey.onmicrosoft.com/?ismsaljsauthenabled=true'

function Hero() {
  const contentRef = useRef<HTMLDivElement | null>(null)

  const scrollToContent = () => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleLetsTalkClick = () => {
    window.open(LETS_TALK_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-[60vh] w-full flex flex-col items-center justify-center text-center">
        {/* Background */}
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url(${bgSpeed})`, filter: 'brightness(0.55)' }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mt-16 px-4">
          <h1
            className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Antonio, sans-serif' }}
          >
            OUR LOGISTICS SERVICES
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl mx-auto">
            From local deliveries to full-scale distribution support, Speedyssey adapts to your needs
          </p>
        </div>

        {/* Down Arrow */}
        <button
          onClick={scrollToContent}
          className="relative z-10 mt-8  flex items-center justify-center  transition-colors"
          aria-label="Scroll to services content"
        >
          <img src={arrowRight} alt="Scroll down" className="w-12 h-12 " />
        </button>
      </section>

      {/* Features Section */}
      <section ref={contentRef} className="bg-white py-10 md:py-20 px-4">
        <div className="container mx-auto">

          {/* Feature 1: Text left, image right */}
          <div className="mb-12 md:mb-24 flex flex-col items-center gap-8 md:gap-16 lg:gap-24 md:flex-row">
            {/* Text */}
            <div className="w-full md:w-7/12 lg:w-1/2">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-6"
                style={{ fontFamily: 'Antonio, sans-serif' }}
              >
                ZONE-BASED DISPATCH
              </h2>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                Our dispatch engine works based on predefined zones in your city or region. This means faster response times, better route optimization, and more predictable pricing based on distance.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                We pair your order with the most optimal nearby dispatch personnel — reducing delays and cost.
              </p>
              <ServiceButton
                title="Let’s Talk Logistics"
                onClick={handleLetsTalkClick}
                className="mt-4"
              />
            </div>
            {/* Image */}
            <div className="relative w-full md:w-5/12 lg:w-1/2">
              <div className="overflow-hidden">
                {/* Use responsive images for better mobile experience */}
                <picture>
                  <source srcSet={imgZone} media="(min-width: 768px)" />
                  <img
                    src={imgZone_mobile}
                    alt="ZONE-BASED DISPATCH"
                    className="w-full object-contain"
                  />
                </picture>
              </div>
              {/* Tagline Overlay (optional, currently commented out) */}
              {/* <div className="absolute bottom-4 left-4 bg-black/80 text-white text-sm md:text-base px-4 py-2 rounded-full flex items-center gap-2">
                <span>Smart delivery routes. Smarter cost control.</span>
              </div> */}
            </div>
          </div>

          {/* Feature 2: Image left, text right */}
          <div className="mb-12 md:mb-24 flex flex-col items-center gap-8 md:gap-16 lg:gap-24 md:flex-row-reverse">
            {/* Text */}
            <div className="w-full md:w-7/12 lg:w-1/2">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-6"
                style={{ fontFamily: 'Antonio, sans-serif' }}
              >
                REAL-TIME TRACKING
              </h2>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                Businesses can track deliveries in real time — from warehouse to doorstep. No guesswork, just reliable updates at every stage. Every movement is logged and timestamped: pickup confirmation, transit location, estimated time of arrival, and successful delivery.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                You’re always in the loop — with instant notifications and live map updates right from your dashboard or mobile app.
              </p>
              <ServiceButton
                title="Let’s Talk Logistics"
                onClick={handleLetsTalkClick}
                className="mt-4"
              />
            </div>
            {/* Image */}
            <div className="relative w-full md:w-5/12 lg:w-1/2">
              <div className="overflow-hidden">
                <picture>
                  <source srcSet={imgTracking} media="(min-width: 768px)" />
                  <img
                    src={imgTracking_mobile}
                    alt="REAL-TIME TRACKING"
                    className="w-full object-contain"
                  />
                </picture>
              </div>
              {/* Tagline Overlay (optional, currently commented out) */}
              {/* <div className="absolute bottom-4 left-4 bg-black/80 text-white text-sm md:text-base px-4 py-2 rounded-full flex items-center gap-2">
                <span>Live Status | Full Transparency</span>
              </div> */}
            </div>
          </div>

          <div className="w-full my-12">
            <picture>
              <source srcSet={imgFullWidth} media="(min-width: 768px)" />
              <img
                src={imgFullWidth_mobile}
                alt="Our Services"
                className="w-full object-contain"
                style={{ maxHeight: '100%', height: 'auto' }}
              />
            </picture>
          </div>

          {/* Feature 3: Text left, image right */}
          <div className="mb-12 md:mb-24 flex flex-col items-center gap-8 md:gap-16 lg:gap-24 md:flex-row-reverse">
            {/* Text */}
            <div className="w-full md:w-7/12 lg:w-1/2">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-6"
                style={{ fontFamily: 'Antonio, sans-serif' }}
              >
                SCHEDULED PICKUPS
              </h2>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                We understand that timing is everything. With Scheduled Pickups, you can choose exact pickup windows that align with your operations or customer preferences.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                This service brings predictability and control to your logistics flow — especially during peak hours or high-demand seasons.
              </p>
              <ServiceButton
                title="Let’s Talk Logistics"
                onClick={handleLetsTalkClick}
                className="mt-4"
              />
            </div>
            {/* Image */}
            <div className="relative w-full md:w-5/12 lg:w-1/2">
              <div className="overflow-hidden">
                <picture>
                  <source srcSet={imgPickup} media="(min-width: 768px)" />
                  <img
                    src={imgPickup_mobile}
                    alt="SCHEDULED PICKUPS"
                    className="w-full object-contain"
                  />
                </picture>
              </div>
            
            </div>
          </div>

        </div>
      </section>
    </div>
  )
}

export default Hero